import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './queryKeys';
import type {
  OwnershipDocumentType,
  PhoneAttestationChannel,
  WorkOrderSignerRole,
  WorkOrderSignatureStatus,
} from '@/types';
import {
  describeWorkOrderSignatureRequiredError,
  isWorkOrderSignatureRequiredError,
  openWorkOrderApprovalCard,
  WORK_ORDER_SIGNATURE_REQUIRED_CODE,
} from '@/components/workorders/signature/signatureRequiredError';

export {
  describeWorkOrderSignatureRequiredError,
  isWorkOrderSignatureRequiredError,
  openWorkOrderApprovalCard,
  WORK_ORDER_SIGNATURE_REQUIRED_CODE,
};

export function workOrderSignatureQueryKey(workOrderId: string) {
  return [...queryKeys.workOrders.detail(workOrderId), 'signature'] as const;
}

export interface TabletSignatureInput {
  signerName: string;
  signerRole: WorkOrderSignerRole;
  signatureImage: string;
  consentTextVersion: number;
}

export interface PaperSignatureInput {
  attachmentId: string;
}

export interface PhoneSignatureInput {
  customerName: string;
  channel: PhoneAttestationChannel;
  receivedAt: string;
  statement: string;
  reference?: string;
}

export interface OverrideSignatureInput {
  action: 'waive' | 'require';
  reason: string;
}

export interface OwnershipVerificationInput {
  documentType: OwnershipDocumentType;
  matchesRegistry: boolean;
  notes?: string;
}

export function useWorkOrderSignature(workOrderId: string | undefined) {
  const queryClient = useQueryClient();

  const invalidate = async () => {
    if (!workOrderId) return;
    await queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.detail(workOrderId) });
  };

  const query = useQuery({
    queryKey: workOrderSignatureQueryKey(workOrderId ?? ''),
    queryFn: () => api.get<WorkOrderSignatureStatus>(`/work-orders/${workOrderId}/signature`),
    enabled: workOrderId != null && workOrderId.length > 0,
  });

  const tablet = useMutation({
    mutationFn: (input: TabletSignatureInput) =>
      api.post(`/work-orders/${workOrderId}/signature/tablet`, input),
    onSuccess: () => invalidate(),
  });

  const paper = useMutation({
    mutationFn: (input: PaperSignatureInput) =>
      api.post(`/work-orders/${workOrderId}/signature/paper`, input),
    onSuccess: () => invalidate(),
  });

  const phone = useMutation({
    mutationFn: (input: PhoneSignatureInput) => {
      const body: Record<string, unknown> = {
        customerName: input.customerName,
        channel: input.channel,
        receivedAt: input.receivedAt,
        statement: input.statement,
      };
      if (input.reference !== undefined) body['reference'] = input.reference;
      return api.post(`/work-orders/${workOrderId}/signature/phone`, body);
    },
    onSuccess: () => invalidate(),
  });

  const override = useMutation({
    mutationFn: (input: OverrideSignatureInput) =>
      api.post(`/work-orders/${workOrderId}/signature/override`, input),
    onSuccess: () => invalidate(),
  });

  const ownership = useMutation({
    mutationFn: (input: OwnershipVerificationInput) => {
      const body: Record<string, unknown> = {
        documentType: input.documentType,
        matchesRegistry: input.matchesRegistry,
      };
      if (input.notes !== undefined) body['notes'] = input.notes;
      return api.post(`/work-orders/${workOrderId}/ownership-verification`, body);
    },
    onSuccess: () => invalidate(),
  });

  return {
    status: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
    signTablet: tablet.mutateAsync,
    tabletPending: tablet.isPending,
    attachPaper: paper.mutateAsync,
    paperPending: paper.isPending,
    recordPhone: phone.mutateAsync,
    phonePending: phone.isPending,
    overrideSignature: override.mutateAsync,
    overridePending: override.isPending,
    recordOwnership: ownership.mutateAsync,
    ownershipPending: ownership.isPending,
  };
}
